import {isPlatformBrowser} from '@angular/common';
import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {CampaignData, ICampaignTrackingService, JsonService} from '@dcp/configurator';

@Injectable({
  providedIn: 'root',
})
export class CampaignTrackingService implements ICampaignTrackingService {
  private jsonService = inject(JsonService);
  public platformId = inject(PLATFORM_ID);

  campaignItemName: string = 'campaignData';
  // Expiration time is in milliseconds (1hour)
  campaignExpiration: number = 60 * 60 * 1000;

  public clearCampaignData(): void {
    if (isPlatformBrowser(this.platformId) && window.sessionStorage) {
      window.sessionStorage.removeItem(this.campaignItemName);
    }
  }

  public saveInSessionStorage(campaignData: object): void {
    if (isPlatformBrowser(this.platformId) && window.sessionStorage) {
      const expires: Date = this.createExpireDate();
      const sessionData: any = {
        expires: expires.toUTCString(),
        data: campaignData,
      };
      window.sessionStorage.setItem(this.campaignItemName, JSON.stringify(sessionData));
    }
  }

  public getCampaignData(): CampaignData {
    if (this.campaignDataExists()) {
      const sessionData: any = this.jsonService.parseSafely(window.sessionStorage.getItem(this.campaignItemName));
      if (Date.parse(sessionData.expires) > new Date().getTime()) {
        return sessionData.data;
      } else {
        this.clearCampaignData();
      }
    }
    return null;
  }

  public setupCampaignTracking(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const query: string = window.location.search;
    const urlCampaignData: CampaignData = this.readCampaignDataFromUrl(query);
    const hasNewData = urlCampaignData.id || urlCampaignData.source || urlCampaignData.url_id;
    if (!hasNewData) return

    if (hasNewData) {
      const updatedData = {
        ...this.getCampaignData(),
        ...urlCampaignData,
      };
      this.saveInSessionStorage(updatedData);
    }
  }

  /**
   * Searches the url query for campaign data and returns a CampaignData.
   *
   * @param query to search the parameters in.
   */
  readCampaignDataFromUrl(query: string): CampaignData {
    const searchParams: URLSearchParams = new URLSearchParams(query);
    const campaignData: CampaignData = new CampaignData();

    campaignData.id = searchParams.get('campaigncode');
    campaignData.source = searchParams.get('source');
    campaignData.url_id = searchParams.get('url_id');
    return campaignData;
  }

  createExpireDate() {
    const expires: Date = new Date();
    expires.setTime(expires.getTime() + this.campaignExpiration);
    return expires;
  }

  toCodeValuePair(campaignData: CampaignData): { code: string; value: string }[] {
    return [
      {code: 'CAMPAIGN_ID', value: campaignData.id},
      {code: 'SOURCE', value: campaignData.source},
      {code: 'URL_ID', value: campaignData.url_id},
    ];
  }

  private campaignDataExists(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      window.sessionStorage &&
      !!window.sessionStorage.getItem(this.campaignItemName)
    );
  }
}
